import React from 'react';
import { Zap, Settings, X, Clock, Plus, HelpCircle, ChevronRight } from 'lucide-react';

interface SkillsSectionProps {
  skills: Array<{ _id: string; name: string; timeLimit: number; difficulty: string; hasQuestions: boolean; questions?: Array<{ id: string; text: string }> }>;
  openEditSkillModal: (skill: any) => void;
  openDeleteSkillModal: (skillId: string) => void;
  openAddSkillModal: () => void;
  onNavigateToSettings?: () => void;
}

const SkillsSection: React.FC<SkillsSectionProps> = ({
  skills,
  openEditSkillModal,
  openDeleteSkillModal,
  openAddSkillModal,
  onNavigateToSettings,
}) => {
  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold text-gray-900">Skills</h2>
            <HelpCircle className="h-4 w-4 text-blue-500" />
          </div>
          <button 
            type="button"
            onClick={openAddSkillModal}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add Skill</span>
          </button>
        </div>

        {/* Skills Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Skill</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Time Limit (mins)</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Difficulty</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {skills.map((skill) => (
                <tr key={skill._id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-4">
                    <div className="font-medium text-gray-900">{skill.name}</div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-1 text-gray-700">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>{skill.timeLimit} mins</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        skill.difficulty === 'Beginner'
                          ? 'bg-green-100 text-green-800'
                          : skill.difficulty === 'Intermediate'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {skill.difficulty}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button 
                        onClick={() => openEditSkillModal(skill)}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        aria-label="Edit skill"
                      >
                        <Settings className="h-4 w-4 text-gray-600" />
                      </button>
                      <button 
                        onClick={() => openDeleteSkillModal(skill._id)}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        aria-label="Delete skill"
                      >
                        <X className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {skills.length === 0 && (
          <div className="text-center py-12">
            <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No skills added yet. Add your first skill to get started.</p>
          </div>
        )}

        {/* Next Button */}
        <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={onNavigateToSettings}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span>Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillsSection;
